import api from './api';
import { Grower, GrowerDetails, DeleteSubGrowerRequest } from '../types';

export interface GrowerCreateData {
  name: string;
  description?: string;
  location?: string;
  address_street?: string;
  address_city?: string;
  address_state?: string;
  address_postal_code?: string;
  address_country?: string;
  website?: string;
  contact_user_id?: number | null;
  parent_id?: number | null;
}

export interface GrowerUpdateData {
  name?: string;
  description?: string;
  location?: string;
  address_street?: string;
  address_city?: string;
  address_state?: string;
  address_postal_code?: string;
  address_country?: string;
  website?: string;
  contact_user_id?: number | null;
  parent_id?: number | null;
}

/** Sensor enriched with which grower (parent or sub) it currently belongs to. */
export interface GrowerFamilySensor {
  id: number;
  name: string;
  device_id: string;
  location: string | null;
  is_active: boolean;
  current_grower_id: number;
  current_grower_name: string;
}

/** User enriched with all family-grower assignments they currently hold. */
export interface GrowerFamilyUser {
  id: number;
  email: string;
  full_name: string | null;
  is_active: boolean;
  is_admin_grower: boolean;
  grower_ids: number[];
  grower_names: string[];
}

/** All sensors + users across a parent grower and its sub-growers. */
export interface GrowerFamily {
  sensors: GrowerFamilySensor[];
  users: GrowerFamilyUser[];
}

export const growerService = {
  async getGrowers(): Promise<Grower[]> {
    const response = await api.get<Grower[]>('/api/growers/');
    return response.data;
  },

  async getGrower(id: number): Promise<Grower> {
    const response = await api.get<Grower>(`/api/growers/${id}`);
    return response.data;
  },

  async getGrowerDetails(id: number): Promise<GrowerDetails> {
    const response = await api.get<GrowerDetails>(`/api/growers/${id}/details`);
    return response.data;
  },

  /** Returns all sensors + users across a parent grower and ALL its sub-growers. */
  async getGrowerFamily(parentId: number): Promise<GrowerFamily> {
    const response = await api.get<GrowerFamily>(`/api/growers/${parentId}/family`);
    return response.data;
  },

  async createGrower(data: GrowerCreateData): Promise<Grower> {
    const response = await api.post<Grower>('/api/growers/', data);
    return response.data;
  },

  async updateGrower(id: number, data: GrowerUpdateData): Promise<Grower> {
    const response = await api.put<Grower>(`/api/growers/${id}`, data);
    return response.data;
  },

  async deleteGrower(id: number): Promise<void> {
    await api.delete(`/api/growers/${id}`);
  },

  // Sub-grower deletion with user reassignment
  async deleteSubGrower(
    parentGrowerId: number,
    subGrowerId: number,
    request: DeleteSubGrowerRequest
  ): Promise<void> {
    await api.delete(`/api/growers/${parentGrowerId}/subgrowers/${subGrowerId}`, {
      data: request,
    });
  },

  // Sensor assignment to sub-grower
  async assignSensorToSubGrower(subGrowerId: number, sensorId: number): Promise<Grower> {
    const response = await api.post<Grower>(`/api/growers/${subGrowerId}/assign-sensor`, {
      sensor_id: sensorId,
    });
    return response.data;
  },

  async unassignSensorFromSubGrower(subGrowerId: number, sensorId: number): Promise<Grower> {
    const response = await api.post<Grower>(`/api/growers/${subGrowerId}/unassign-sensor`, {
      sensor_id: sensorId,
    });
    return response.data;
  },

  // User assignment to sub-grower
  async assignUserToSubGrower(subGrowerId: number, userId: number): Promise<Grower> {
    const response = await api.post<Grower>(`/api/growers/${subGrowerId}/assign-user`, {
      user_id: userId,
    });
    return response.data;
  },

  async unassignUserFromSubGrower(subGrowerId: number, userId: number): Promise<Grower> {
    const response = await api.post<Grower>(`/api/growers/${subGrowerId}/unassign-user`, {
      user_id: userId,
    });
    return response.data;
  },
};
